import {
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import type { UsedExperienceDto } from '@repo/shared/dtos/analytics/ExperienceFilterDto';
import { filterStorage } from '../utils/filterStorage';
import { urlSync } from '../utils/urlSync';
import { useFilterUnits } from '../hooks/useFilterUnits';
import { createDefaultFilters, hasActiveFilters } from '../filterHelpers';
import { API_ENDPOINTS, QUERY_KEYS, CACHE_CONFIG, RETRY_CONFIG } from '../constants/endpoints';
import { useSession } from '../../../../hooks/auth/useSession';

export interface FiltersContextType {
  filters: BaseAnalyticsFilter;
  updateFilters: (updates: Partial<BaseAnalyticsFilter>) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  setFilter: <K extends keyof BaseAnalyticsFilter>(key: K, value: BaseAnalyticsFilter[K]) => void;
  getUnitName: (id: number) => string;
  getExperienceName: (id: number) => string;
  isLoading: boolean;
  error: Error | null;
  isInitialized: boolean;
}

const DEFAULT_FILTERS = createDefaultFilters();

const FiltersContext = createContext<FiltersContextType | null>(null);

export function FiltersProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useSession();
  const [filters, setFilters] = useState<BaseAnalyticsFilter>(DEFAULT_FILTERS);
  const [isInitialized, setIsInitialized] = useState(false);

  const {
    data: units,
    isLoading: unitsLoading,
    error: unitsError,
  } = useFilterUnits(isAuthenticated);

  const {
    data: experiences,
    isLoading: experiencesLoading,
    error: experiencesError,
  } = useQuery<UsedExperienceDto[]>({
    queryKey: QUERY_KEYS.FILTERS_EXPERIENCES,
    queryFn: async () => {
      const response = await fetch(API_ENDPOINTS.USED_EXPERIENCES, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch experiences');
      const result = await response.json();
      return Array.isArray(result) ? result : result.experiences || result.items || [];
    },
    enabled: isAuthenticated,
    staleTime: CACHE_CONFIG.EXPERIENCES.staleTime,
    gcTime: CACHE_CONFIG.EXPERIENCES.gcTime,
    retry: RETRY_CONFIG.shouldRetry,
    retryDelay: RETRY_CONFIG.retryDelay,
  });

  useEffect(() => {
    const urlFilters = urlSync.loadFiltersFromUrl();

    if (urlSync.hasUrlFilters()) {
      setFilters(prev => ({ ...prev, ...urlFilters }));
    } else if (filterStorage.isAvailable()) {
      const stored = filterStorage.load();
      if (stored) {
        setFilters(prev => ({ ...prev, ...stored }));
      }
    }

    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      urlSync.saveFiltersToUrl(filters);
      if (filterStorage.isAvailable()) {
        filterStorage.save(filters);
      }
    }
  }, [filters, isInitialized]);

  const updateFilters = useCallback((updates: Partial<BaseAnalyticsFilter>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      startDate: undefined,
      endDate: undefined,
      experienceIds: [],
      unitIds: [],
      experienceTitle: undefined,
      granularity: 'month',
    });
  }, []);

  const setFilter = useCallback(
    <K extends keyof BaseAnalyticsFilter>(key: K, value: BaseAnalyticsFilter[K]) => {
      setFilters(prev => ({ ...prev, [key]: value }));
    },
    [],
  );

  const getUnitName = useMemo(() => {
    const unitMap = new Map<number, string>();
    units?.forEach(unit => {
      unitMap.set(parseInt(unit.unitid), unit.unitdescription || `Unit ${unit.unitid}`);
    });

    return (id: number): string => unitMap.get(id) || `Unit ${id}`;
  }, [units]);

  const getExperienceName = useMemo(() => {
    const experienceMap = new Map<number, string>();
    experiences?.forEach(experience => {
      experienceMap.set(experience.experienceId, experience.experienceTitle);
    });

    return (id: number): string => experienceMap.get(id) || `Experience ${id}`;
  }, [experiences]);

  const hasFiltersActive = useMemo(() => hasActiveFilters(filters), [filters]);

  const filtersData = useMemo(
    () => ({
      filters,
      hasActiveFilters: hasFiltersActive,
      getUnitName,
      getExperienceName,
    }),
    [filters, hasFiltersActive, getUnitName, getExperienceName],
  );

  const filtersActions = useMemo(
    () => ({
      updateFilters,
      clearFilters,
      setFilter,
    }),
    [updateFilters, clearFilters, setFilter],
  );

  const loadingState = useMemo(
    () => ({
      isLoading: unitsLoading || experiencesLoading,
      error: unitsError || experiencesError,
    }),
    [unitsLoading, experiencesLoading, unitsError, experiencesError],
  );

  const value = useMemo(
    () => ({
      ...filtersData,
      ...filtersActions,
      ...loadingState,
      isInitialized,
    }),
    [filtersData, filtersActions, loadingState, isInitialized],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useAnalyticsFilters(): FiltersContextType {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useAnalyticsFilters must be used within FiltersProvider');
  }
  return context;
}

export function useFilterData() {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFilterData must be used within FiltersProvider');
  }
  return {
    getUnitName: context.getUnitName,
    getExperienceName: context.getExperienceName,
    isLoading: context.isLoading,
    error: context.error,
  };
}
